import { Component, EventEmitter, Input, OnChanges, Output, ViewChild } from '@angular/core';
import * as moment from 'moment';
import { SettingsService } from 'src/app/services/settings.service';
import { TransactionService } from 'src/app/services/transaction.service';
import { ChartColors } from 'src/app/shared/models/general/chart-colors';
import { ChartData } from 'src/app/shared/models/general/chart-data';
import { SearchRequest } from 'src/app/shared/models/general/search-request';
import { TransactionDataBundle } from 'src/app/shared/models/transactions/transaction-data-bundle';
import { UserProfile } from 'src/app/shared/models/users/user-profile';
import { Error } from 'src/app/shared/models/general/error';
import { FiaLeftToApplyFor } from 'src/app/shared/models/users/fia-left-to-apply-for';
import { FiaService } from 'src/app/services/fia.service';

@Component({
  selector: 'app-user-summary',
  templateUrl: './user-summary.component.html',
  styleUrls: ['./user-summary.component.scss'],
})
export class UserSummaryComponent implements OnChanges {
  @ViewChild('userTradeSummary', { static: true }) userTradeSummary: any;
  @Input() selectedUserProfile: UserProfile;
  @Input() showModal: boolean = false;
  @Output() showModalChange = new EventEmitter<boolean>();

  //General
  busy: boolean = false;
  welcomCardBusy: boolean = false;
  statCardsBusy: boolean = true;
  showError: boolean = false;
  errorMsg = '';
  currentYear = new Date().getFullYear().toString();
  traded: boolean = false;
  userProfile: UserProfile = new UserProfile();
  fiaLeftToApplyFor: FiaLeftToApplyFor = new FiaLeftToApplyFor();

  //Weekly Stat cards
  statCardsSearchRequest: SearchRequest = new SearchRequest();
  statCardsTransactionDataBundle: TransactionDataBundle[] = [];
  statCardsFiaAvailable: number = 0;
  statCardsSdaAvailable: number = 0;

  //Main yearly summary bar chart
  public chart1Type: string = 'bar';
  public chartDatasets: Array<ChartData> = [];
  public chartLabels: Array<any> = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  public chartColors: ChartColors[] = [];
  public chartOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      yAxes: [
        {
          ticks: {
            beginAtZero: true,
            callback: function (value: number) {
              return 'R' + value.toLocaleString();
            },
          },
        },
      ],
    },
    tooltips: {
      callbacks: {
        label: function (tooltipItem: any) {
          var data: number = tooltipItem.yLabel;
          return 'R ' + data.toLocaleString();
        },
      },
    },
  };
  yearlySummarySearchRequest: SearchRequest = new SearchRequest();
  yearlySummaryTransactionDataBundle: TransactionDataBundle[] = [];
  yearlySummaryBusy: boolean = false;

  constructor(
    public settingsService: SettingsService,
    public transactionsService: TransactionService,
    public fiaService: FiaService
  ) {}

  ngOnChanges() {
    this.showHideModal(null);
  }

  onClosed(event: any) {
    if (this.showModal != false) {
      this.showModal = false;
      this.showModalChange.emit(this.showModal);
    }
  }

  showHideModal(toggle: boolean) {
    if (toggle != null) {
      this.showModal = toggle;
    }

    if (this.showModal) {
      this.userTradeSummary.show();
      this.userProfile = <UserProfile>JSON.parse(JSON.stringify(this.selectedUserProfile));
      this.GetTransactionData();
      this.GetLeftToApplyFor();
    } else if (!this.showModal) {
      // Blur any focused element before hiding modal to prevent aria-hidden warning
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      this.clearErrors();
      this.userTradeSummary.hide();
    }

    this.showModalChange.emit(this.showModal);
  }

  GetTransactionData() {
    this.statCardsBusy = true;
    this.yearlySummaryBusy = true;
    this.clearErrors();

    this.statCardsSearchRequest.id = this.userProfile.userId;
    this.statCardsSearchRequest.entityType = this.settingsService.userEntity.id;
    this.statCardsSearchRequest.timePeriod = 2; //Month
    this.statCardsSearchRequest.fromDate = moment().startOf('year').format('YYYY-MM-DD');
    this.statCardsSearchRequest.toDate = moment().format('YYYY-MM-DD');

    this.transactionsService
      .GetTransactionDataBundleForEntity(this.statCardsSearchRequest)
      .toPromise()
      .then(
        (response) => {
          this.statCardsTransactionDataBundle = response;
          //this.statCardsFiaAvailable = this.statCardsTransactionDataBundle[0].fiaAvailable.fiaAvailable;
          this.statCardsFiaAvailable = this.statCardsTransactionDataBundle[0].tradingStatistics.fiaAvailable;
          this.statCardsSdaAvailable = this.statCardsTransactionDataBundle[0].tradingStatistics.sdaAvailable;
          this.statCardsBusy = false;
          this.SetupYearlySummaryGraph(this.statCardsTransactionDataBundle).then(() => {
            this.yearlySummaryBusy = false;
          });
        },
        (error) => {
          this.displayError(error);
          this.statCardsBusy = false;
          this.yearlySummaryBusy = false;
        }
      );
  }

  SetupYearlySummaryGraph(data: TransactionDataBundle[]) {
    return new Promise((resolve) => {
      this.chartDatasets = [];
      var chartdata: ChartData = new ChartData();
      var chartColor: ChartColors = new ChartColors();

      this.traded = data.find((x) => x.noOfTrades > 0) != undefined ? true : false;
      data.forEach((x) => {
        chartdata.data.push(x.totalTradeProfit);

        chartColor.backgroundColor.push('rgba(0, 200, 81, 0.2)');
        chartColor.borderColor.push('rgba(0, 200, 81, 1)');
      });

      chartdata.label = 'Total Monthly Profit';
      this.chartDatasets.push(chartdata);
      this.chartColors.push(chartColor);
      resolve(true);
    });
  }

  GetLeftToApplyFor() {
    return new Promise((resolve) => {
      this.busy = true;
      this.clearErrors();
      this.fiaLeftToApplyFor = null;
      this.fiaService.CalculateLeftToApplyFor(this.userProfile.userId).subscribe({
        next: (response: FiaLeftToApplyFor) => {
          this.fiaLeftToApplyFor = response;
          this.busy = false;
          resolve(true);
        },
        error: (error) => {
          this.displayError(error);
          this.busy = false;
          resolve(true);
        },
      });
    });
  }

  clearErrors() {
    this.errorMsg = '';
    this.showError = false;
  }

  displayError(error: any) {
    this.showError = true;
    this.errorMsg = Error.InterpretErrorMessage(error);
  }
}
